import { CheckCircleOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Spin, Typography } from 'antd';
import * as React from 'react';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';

import { ToastVariant } from '../../../components/Toast/toast.model';
import { toastService } from '../../../components/Toast/toast.service';
import { firebaseFunctions, firestore } from '../../../firebase/firebase';
import { FirebaseCallableFunctions, FirestoreCollection } from '../../../firebase/firebase.models';
import { useAuth } from '../../Auth/AuthContext';
import { AppRoutes } from '../../Routing/routing.model';

const { Text, Title } = Typography;

interface Props {
  offerId: string;
}

interface ClientFormData {
  name: string;
  address: string;
  peselOrNip: string;
  contact: string;
  email: string;
  includeMediation: boolean;
}

export const AgentClientSection: React.FC<Props> = ({ offerId }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [checkingAgreement, setCheckingAgreement] = useState<boolean>(true);
  const [hasExistingAgreement, setHasExistingAgreement] = useState<boolean>(false);
  const [agreementData, setAgreementData] = useState<any>(null);
  const [form] = Form.useForm<ClientFormData>();
  const includeMediation = Form.useWatch('includeMediation', form);
  const { push } = useHistory();
  const { currentUser } = useAuth();

  // Check if agent already has an agreement for this offer
  useEffect(() => {
    if (!currentUser?.uid || !offerId) {
      setCheckingAgreement(false);
      return;
    }

    const checkExistingAgreement = async () => {
      try {
        // Query for existing agency agreements for this offer and agent
        const agreementsSnapshot = await firestore
          .collection(FirestoreCollection.AGENCY_AGREEMENTS)
          .where('offerId', '==', offerId)
          .where('agentUid', '==', currentUser.uid)
          .where('status', '==', 'signed')
          .limit(1)
          .get();

        if (!agreementsSnapshot.empty) {
          const agreement = agreementsSnapshot.docs[0].data();
          setAgreementData(agreement);
          setHasExistingAgreement(true);
        }
      } catch (error) {
        console.log('Error checking for existing agreement:', error);
        // In case of error (e.g., no permissions), show the form anyway
      } finally {
        setCheckingAgreement(false);
      }
    };

    checkExistingAgreement();
  }, [currentUser, offerId]);

  const handleSubmit = async (values: ClientFormData) => {
    setLoading(true);

    try {
      // Debug: log the form values to verify checkbox state
      console.log('Form values:', values);
      console.log('includeMediation value:', values.includeMediation);

      const payload = {
        offerId: offerId,
        ownerDetails: {
          name: values.name,
          address: values.address,
          peselOrNip: values.peselOrNip,
          contact: values.contact,
          email: values.email,
        },
        includeMediation: Boolean(values.includeMediation),
      };

      console.log('Payload being sent:', payload);

      await firebaseFunctions.httpsCallable(FirebaseCallableFunctions.LINK_PROJECT_OFFER_FOR_AGENT)(payload);

      toastService.show('Propozycja współpracy została wysłana', 'Sukces', { variant: ToastVariant.SUCCESS });

      form.resetFields();
      push(AppRoutes.MOJA_TABLICA_UNSIGNED_OFFERS);
    } catch (error) {
      console.error('Error sending agent proposal:', error);
      toastService.show('Wystąpił błąd podczas wysyłania propozycji', 'Błąd', { variant: ToastVariant.ERROR });
    } finally {
      setLoading(false);
    }
  };

  // Loading state while checking for existing agreement
  if (checkingAgreement) {
    return (
      <Card className="mt-16 shadow-sm border border-gray-200">
        <div className="flex justify-center items-center py-8">
          <Spin tip="Sprawdzanie istniejących umów..." />
        </div>
      </Card>
    );
  }

  // Show owner details if agent already has a signed agreement for this offer
  if (hasExistingAgreement && agreementData) {
    return (
      <Card className="mt-16 shadow-sm border border-gray-200 bg-green-50">
        <div className="space-y-4">
          {/* Header with success icon */}
          <div className="flex items-center space-x-3 pb-4 border-b border-green-200">
            <CheckCircleOutlined className="text-green-600 text-2xl" />
            <div>
              <Title level={5} className="!mb-1">
                Pełnomocnictwo zostało podpisane
              </Title>
              <Text className="text-xs text-gray-600">
                Posiadasz aktywne pełnomocnictwo dla tej oferty i możesz działać w imieniu właściciela
              </Text>
            </div>
          </div>

          {/* Owner details */}
          <div>
            <Title level={5} className="text-gray-700 mb-3">
              Dane mocodawcy (właściciela)
            </Title>
            <div className="space-y-2 text-sm bg-white p-4 rounded border border-gray-200">
              <div className="flex">
                <Text className="text-gray-600 w-48">Imię i nazwisko/nazwa:</Text>
                <Text strong>{agreementData?.ownerDetails?.name}</Text>
              </div>
              <div className="flex">
                <Text className="text-gray-600 w-48">Adres:</Text>
                <Text strong>{agreementData?.ownerDetails?.address}</Text>
              </div>
              <div className="flex">
                <Text className="text-gray-600 w-48">PESEL/NIP:</Text>
                <Text strong>{agreementData?.ownerDetails?.peselOrNip}</Text>
              </div>
              <div className="flex">
                <Text className="text-gray-600 w-48">Telefon:</Text>
                <Text strong>{agreementData?.ownerDetails?.contact}</Text>
              </div>
              <div className="flex">
                <Text className="text-gray-600 w-48">Email:</Text>
                <Text strong>{agreementData?.ownerDetails?.email}</Text>
              </div>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card
      className="mt-16 shadow-sm border border-gray-200"
      title={
        <div>
          <Title level={5}>Dane właściciela nieruchomości</Title>
          <Text className="text-xs text-gray-500">Wprowadź dane właściciela, aby wysłać propozycję współpracy</Text>
        </div>
      }>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ includeMediation: false }}
        onValuesChange={(changedValues) => {
          // Debug: log when checkbox value changes
          if ('includeMediation' in changedValues) {
            console.log('Checkbox changed to:', changedValues.includeMediation);
          }
        }}
        className="space-y-4">
        <Form.Item
          name="name"
          label="Imię i nazwisko lub nazwa podmiotu"
          rules={[{ required: true, message: 'To pole jest wymagane' }]}>
          <Input placeholder="np. Jan Kowalski lub Firma Sp. z o.o." />
        </Form.Item>

        <Form.Item
          name="address"
          label="Adres zamieszkania/siedziby"
          rules={[{ required: true, message: 'To pole jest wymagane' }]}>
          <Input.TextArea rows={2} placeholder="ul. Przykładowa 1/2, 00-001 Warszawa" />
        </Form.Item>

        <div className="grid grid-cols-2 gap-4">
          <Form.Item
            name="peselOrNip"
            label="PESEL/NIP"
            rules={[
              { required: true, message: 'To pole jest wymagane' },
              {
                pattern: /^(\d{11}|\d{10})$/,
                message: 'Wprowadź poprawny PESEL (11 cyfr) lub NIP (10 cyfr)',
              },
            ]}>
            <Input placeholder="np. 12345678901 lub 1234567890" />
          </Form.Item>

          <Form.Item
            name="contact"
            label="Telefon kontaktowy"
            rules={[
              { required: true, message: 'To pole jest wymagane' },
              { pattern: /^[\d\s+\-()]+$/, message: 'Wprowadź poprawny numer telefonu' },
            ]}>
            <Input placeholder="np. +48 123 456 789" />
          </Form.Item>
        </div>

        <Form.Item
          name="email"
          label="Email właściciela"
          rules={[
            { required: true, message: 'To pole jest wymagane' },
            { type: 'email', message: 'Wprowadź poprawny adres email' },
          ]}>
          <Input type="email" placeholder="email@przykład.pl" />
        </Form.Item>

        {/* <Form.Item name="includeMediation" valuePropName="checked">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Checkbox>Dołącz umowę pośrednictwa do pełnomocnictwa</Checkbox>
              <Tooltip
                title={
                  <div>
                    <strong>Samo pełnomocnictwo:</strong>
                    <br />• Upoważnia do działania w imieniu właściciela
                    <br />• Nie zawiera zapisów o wynagrodzeniu
                    <br />• Prostszy dokument do akceptacji
                    <br />
                    <br />
                    <strong>Pełnomocnictwo z umową pośrednictwa:</strong>
                    <br />• Zawiera dodatkowo umowę o świadczenie usług
                    <br />• Określa wynagrodzenie prowizyjne (100% miesięcznego czynszu)
                    <br />• Kompleksowe uregulowanie współpracy
                  </div>
                }
                placement="top">
                <InfoCircleOutlined className="text-blue-500 cursor-help" />
              </Tooltip>
            </div>
            <Text className="text-xs text-gray-500 ml-6">
              Zaznaczając tę opcję, do dokumentu pełnomocnictwa zostanie dołączona umowa pośrednictwa w obrocie
              nieruchomościami
            </Text>
          </div>
        </Form.Item> */}

        {includeMediation && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded mb-4">
            <Text className="text-xs text-yellow-800">
              <strong>⚠️ Uwaga:</strong> Umowa pośrednictwa zawiera zapisy o wynagrodzeniu prowizyjnym. Właściciel
              będzie musiał zaakceptować warunki finansowe współpracy.
            </Text>
          </div>
        )}

        <div className="p-3 bg-blue-50 rounded">
          <Text className="text-xs text-gray-700">
            <strong>Informacja:</strong> Po wysłaniu propozycji właściciel otrzyma email z:
          </Text>
          <ul className="text-xs text-gray-600 mt-2 ml-4 list-disc">
            <li>Linkiem do uzupełnienia oferty na platformie</li>
            <li>
              {includeMediation
                ? 'Dokumentem pełnomocnictwa z umową pośrednictwa (zawiera klauzule o wynagrodzeniu)'
                : 'Dokumentem pełnomocnictwa'}
            </li>
            <li>Możliwością odpowiedzi bezpośrednio na Twój email (Reply-To)</li>
          </ul>
        </div>

        <Form.Item className="!mb-0">
          <Button type="primary" htmlType="submit" loading={loading} className="w-full">
            Wyślij propozycję współpracy
          </Button>
        </Form.Item>
      </Form>
    </Card>
  );
};
